import { useFliptBoolean, useFliptSelector } from '@flipt-io/flipt-client-react'
import { type FlagKey, ANONYMOUS_ENTITY_ID, buildFliptContext } from '@gozoki/feature-flags'
import { type PropsWithChildren, useContext } from 'react'
import { AuthContext } from '~/utils/context/auth.context'

/**
 * Évalue un feature flag boolean Flipt pour l'admin connecté.
 * Rend `fallback` si le client Flipt n'est pas disponible (pas de token,
 * serveur injoignable sans snapshot, chargement en cours).
 *
 * @example const showNewFeature = useFeatureFlag(FLAGS.featureFlags)
 */
export const useFeatureFlag = (flag: FlagKey, fallback = false): boolean => {
  const { user } = useContext(AuthContext)
  return useFliptBoolean(
    flag,
    fallback,
    user?.id.toString() ?? ANONYMOUS_ENTITY_ID,
    buildFliptContext(user),
  )
}

/**
 * Le client Flipt a-t-il fini de charger ? À tester **avant** de rediriger sur un flag à faux :
 * pendant l'initialisation, `useFeatureFlag` rend son fallback, et une page qui décide à ce
 * moment-là s'expulse elle-même avant de connaître la réponse (mesuré : 4 rendus à `false` puis
 * `true`). Un `<Navigate replace>` posé sur ce faux transitoire est irréversible — la page devient
 * inatteignable à froid pour un compte que le flag autorise.
 *
 * Une erreur d'initialisation compte comme « prêt » : le fallback fermé est alors la réponse, et
 * sans ce cas le spinner tournerait indéfiniment.
 */
export const useFeatureFlagsReady = (): boolean =>
  useFliptSelector((_client, isLoading, error) => !isLoading || Boolean(error))

/**
 * Évaluateur de flags réutilisable, pour les cas où un `useFeatureFlag` par flag est impossible :
 * itérer sur une liste (les entrées du menu latéral) violerait les règles des hooks.
 *
 * Un item sans flag est toujours actif — même convention que le composant `FeatureFlag`.
 */
export const useFeatureFlagEvaluator = (): ((flag?: FlagKey, fallback?: boolean) => boolean) => {
  const { user } = useContext(AuthContext)
  const client = useFliptSelector((fliptClient, isLoading, error) =>
    isLoading || error ? null : fliptClient,
  )
  const entityId = user?.id.toString() ?? ANONYMOUS_ENTITY_ID
  const context = buildFliptContext(user)

  return (flag?: FlagKey, fallback = false) => {
    if (!flag) return true
    if (!client) return fallback
    try {
      return client.evaluateBoolean({ flagKey: flag, entityId, context }).enabled
    } catch (error) {
      console.error(`Error evaluating boolean flag ${flag}:`, error)
      return fallback
    }
  }
}

const FeatureFlagGate = ({ flagKey, children }: PropsWithChildren<{ flagKey: FlagKey }>) => {
  const isEnabled = useFeatureFlag(flagKey)
  return isEnabled ? children : null
}

/** Rend les children si le flag est activé (ou si aucun flag n'est fourni). */
export const FeatureFlag = ({
  flagKey,
  children,
}: PropsWithChildren<{ flagKey: FlagKey | undefined }>) =>
  flagKey ? <FeatureFlagGate flagKey={flagKey}>{children}</FeatureFlagGate> : children
